import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import { clipAudioApi } from '../../services/api';
import { colors } from '../../Colors';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  Fab,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Book as BookIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Star as StarIcon
} from '@mui/icons-material';
import StartNewModal from '../../components/ClipAudioWorkflow/StepsComponents/StartNewModal';

function ClipAudioProjectsScreen() {
  const navigate = useNavigate();
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Project state
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stepFilter, setStepFilter] = useState('all');
  const [styleFilter, setStyleFilter] = useState('all');



  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await clipAudioApi.getAllProjects();
        setProjects(data);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(err.message);
        // Fallback to empty array if API fails
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const handleStartNew = () => {
    setIsModalOpen(true);
  };

  const handleStartFromTheme = async (projectName, theme, style, imageFormat) => {
    try {
      setLoading(true);
      setError(null);
      
      const newProject = await clipAudioApi.createProject(projectName, theme, style, imageFormat);
      
      // Ensure new projects start at step 1 if backend doesn't set it
      if (newProject.current_step === 0 || newProject.current_step === undefined) {
        newProject.current_step = 1;
      }
      
      setProjects([newProject, ...projects]);
      // Navigate to pipeline work view
      navigate(`/audio/${newProject.id}`);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error creating project:', err);
      setError(`Erreur lors de la création du projet: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartFromLyric = async (projectName, theme, lyrics, style, imageFormat) => {
    try {
      setLoading(true);
      setError(null);
      
      const newProject = await clipAudioApi.createProjectWithLyrics(projectName, theme, lyrics, style, imageFormat);
      
      // Ensure new projects start at step 1 if backend doesn't set it
      if (newProject.current_step === 0 || newProject.current_step === undefined) {
        newProject.current_step = 1;
      }
      
      setProjects([newProject, ...projects]);
      // Navigate to pipeline work view
      navigate(`/audio/${newProject.id}`);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error creating project:', err);
      setError(`Erreur lors de la création du projet: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (project) => {
    // Navigate to pipeline work view
    navigate(`/audio/pipeline/${project.id}`);
  };

  const handleProjectView = (project) => {
    navigate(`/audio/${project.id}`);
  };



  const getStepLabel = (step) => {
    switch (step) {
      case 0: return "Terminé";
      case 1: return "Paroles";
      case 2: return "Scènes";
      case 3: return "Images";
      case 4: return "Animation";
      default: return "Terminé";
    }
  };

  // Get step emoji and color
  const getStepEmoji = (step) => {
    switch (step) {
      case 0:
        return {
          emoji: '✅',
          bgColor: '#27AE60' // Green for completed
        };
      case 1:
        return {
          emoji: '🎵',
          bgColor: '#FF6B9D' // Pink for lyrics
        };
      case 2:
        return {
          emoji: '🎬',
          bgColor: '#F39C12' // Orange for scenes
        };
      case 3:
        return {
          emoji: '🖼️',
          bgColor: '#3498DB' // Blue for images
        };
      case 4:
        return {
          emoji: '🎞️',
          bgColor: '#9B59B6' // Purple for animation
        };
      default:
        return {
          emoji: '📝',
          bgColor: colors.primary.main
        };
    }
  };

  // Get style label in French
  const getStyleLabel = (style) => {
    if (!style) return '';
    const styleLower = style.toLowerCase();
    switch (styleLower) {
      case 'cartoon':
        return '🎨 Cartoon';
      case 'realistic':
        return '📸 Réaliste';
      case 'semi-realistic':
        return '🎭 Semi-réaliste';
      default:
        return style;
    }
  };

  // Get unique steps for filter options
  const uniqueSteps = [...new Set(projects.map(p => p.current_step).filter(s => s !== undefined))].sort();

  // Get unique styles for filter options
  const uniqueStyles = [...new Set(
    projects
      .map(p => p.style || p.workflow_data?.imageStyle)
      .filter(s => s !== undefined && s !== null)
  )].sort();

  // Filter projects based on search query, step, and style
  const filteredProjects = projects.filter(project => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        project.name?.toLowerCase().includes(query) ||
        project.theme?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }

    // Step filter
    if (stepFilter !== 'all') {
      if (project.current_step !== parseInt(stepFilter)) return false;
    }

    // Style filter
    if (styleFilter !== 'all') {
      const projectStyle = project.style || project.workflow_data?.imageStyle;
      if (projectStyle?.toLowerCase() !== styleFilter.toLowerCase()) return false;
    }

    return true;
  });

  // Render project list view
  return (
    <div style={{ minHeight: '100vh', background: colors.background.main }}>
      <Navigation />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h3" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold', 
              color: colors.text.primary,
              mb: 1
            }}
          >
            Pipeline Audio
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: colors.text.secondary,
              mb: 3
            }}
          >
            Gérez vos projets de clips audio avec intelligence artificielle
          </Typography>

          {/* Search and Filters */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Rechercher un projet, un thème..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                minWidth: 300,
                bgcolor: colors.background.secondary,
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: colors.border.light,
                  },
                  '&:hover fieldset': {
                    borderColor: colors.border.medium,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.text.secondary }} />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl 
              sx={{ 
                minWidth: 180,
                bgcolor: colors.background.secondary,
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.light,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.medium,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                },
              }}
            >
              <InputLabel sx={{ color: colors.text.secondary }}>Toutes les étapes</InputLabel>
              <Select
                value={stepFilter}
                onChange={(e) => setStepFilter(e.target.value)}
                label="Toutes les étapes"
                sx={{ 
                  color: colors.text.primary,
                  '& .MuiSvgIcon-root': {
                    color: colors.text.secondary,
                  },
                }}
              >
                <MenuItem value="all">Toutes les étapes</MenuItem>
                {uniqueSteps.map((step) => (
                  <MenuItem key={step} value={step}>
                    {getStepEmoji(step).emoji} {getStepLabel(step)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl 
              sx={{ 
                minWidth: 180,
                bgcolor: colors.background.secondary,
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.light,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.border.medium,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.primary.main,
                },
              }}
            >
              <InputLabel sx={{ color: colors.text.secondary }}>Tous les styles</InputLabel>
              <Select
                value={styleFilter}
                onChange={(e) => setStyleFilter(e.target.value)}
                label="Tous les styles"
                   sx={{
                  color: colors.text.primary,
                  '& .MuiSvgIcon-root': {
                    color: colors.text.secondary,
                  },
                }}
              >
                <MenuItem value="all">Tous les styles</MenuItem>
                {uniqueStyles.map((style) => (
                  <MenuItem key={style} value={style}>
                    {getStyleLabel(style)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
              </Box>
            </Box>

            {/* Projects Grid */}
            {loading ? (
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} sx={{ mb: 2, color: colors.primary.main }} />
              <Typography variant="h4" sx={{ mb: 2, color: colors.text.primary, fontWeight: 'bold' }}>
                  Chargement des projets...
                </Typography>
              <Typography variant="h6" sx={{ color: colors.text.secondary }}>
                  Veuillez patienter pendant que nous récupérons vos projets.
                </Typography>
            </CardContent>
          </Card>
            ) : error ? (
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Erreur lors du chargement des projets
                  </Typography>
                  <Typography variant="body1">
                    {error}
                  </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: colors.text.secondary }}>
                    Vérifiez que le serveur API est démarré
                  </Typography>
                </Alert>
                <Button
                  variant="contained"
                  onClick={() => window.location.reload()}
                  sx={{
                  bgcolor: colors.primary.main,
                  '&:hover': { bgcolor: colors.primary.dark }
                  }}
                >
                  Réessayer
                </Button>
            </CardContent>
          </Card>
        ) : filteredProjects.length > 0 ? (
          <Box 
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(4, 1fr)'
              },
              gap: {
                xs: 2,
                sm: 2.5,
                md: 2.5
              },
              width: '100%',
              maxWidth: {
                xs: '100%',
                sm: '100%',
                md: '1600px',
                lg: '1700px'
              },
              mx: 'auto'
            }}
          >
            {filteredProjects.map((project) => {
              const stepEmoji = getStepEmoji(project.current_step || 0);
              return (
                <Box 
                  key={project.id}
                  sx={{
                    display: 'flex',
                    minHeight: 0
                  }}
                >
                  <Card 
                    sx={{ 
                      borderRadius: 3,
                      bgcolor: colors.background.secondary,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      width: '100%',
                      maxWidth: {
                        xs: '100%',
                        sm: '100%',
                        md: '100%',
                        lg: '100%'
                      },
                      display: 'flex',
                      flexDirection: 'column',
                      height: {
                        xs: 280,
                        sm: 290,
                        md: 300
                      },
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)'
                      }
                    }}
                  >
                    <CardContent 
                      sx={{ 
                        p: 2, 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        minHeight: 0,
                        height: '100%'
                      }}
                    >
                      {/* Content Type Badges */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mb: 1 }}>
                        <Chip
                          label="Audio"
                          size="small"
                          sx={{
                            bgcolor: colors.status.warning,
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 20,
                            fontWeight: 'bold'
                          }}
                        />
                      </Box>

                      {/* Icon and Title */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 4 }}>
                        <Box
                          sx={{
                            width: 40, 
                            height: 40,
                            borderRadius: 2,
                            bgcolor: stepEmoji.bgColor,
                            display: 'flex', 
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            fontSize: '24px'
                          }}
                        >
                          {stepEmoji.emoji}
                        </Box>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: colors.text.primary,
                            lineHeight: 1.3,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            fontSize: '0.95rem'
                          }}
                        >
                          {project.name}
                        </Typography>
                      </Box>

                      {/* Tags */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                        {project.theme && (
                          <Chip
                            label={project.theme}
                            size="small"
                            sx={{
                              bgcolor: '#FFD700',
                              color: colors.text.primary,
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        )}
                        <Chip
                          label={getStepLabel(project.current_step || 0)}
                          size="small"
                          sx={{
                            bgcolor: '#FFD700',
                            color: colors.text.primary,
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            height: 24
                          }}
                        />
                        {(project.style || project.workflow_data?.imageStyle) && (
                          <Chip
                            label={getStyleLabel(project.style || project.workflow_data?.imageStyle)}
                            size="small"
                            sx={{
                              bgcolor: '#FFD700',
                              color: colors.text.primary,
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        )}
                      </Box>

                      {/* Description */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: colors.text.secondary,
                          mb: 0.25,
                          lineHeight: 1.5,
                          fontSize: '0.8rem'
                        }}
                      >
                        Projet de clip audio en cours de création
                      </Typography>

                      {/* Metadata */}
                      <Box sx={{ display: 'flex', gap: 1.5, mb: 0.25, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ScheduleIcon sx={{ fontSize: 16, color: colors.text.secondary }} />
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            {project.current_step || 0}/4
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <StarIcon sx={{ fontSize: 16, color: colors.status.warning }} />
                          <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                            ★4.5
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Action Button */}
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<BookIcon />}
                        onClick={() => handleProjectSelect(project)}
                        sx={{
                          bgcolor: colors.primary.main,
                          color: 'white',
                          borderRadius: 2,
                          py: 0.75,
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                          textTransform: 'none',
                          mt: 'auto',
                          '&:hover': {
                            bgcolor: colors.primary.dark,
                          }
                        }}
                      >
                        Continuer le projet
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h1" sx={{ mb: 2, fontSize: '4rem' }}>🎵</Typography>
              <Typography variant="h4" sx={{ mb: 2, color: colors.text.primary, fontWeight: 'bold' }}>
                {searchQuery || stepFilter !== 'all' ? 'Aucun résultat trouvé' : 'Aucun projet pour le moment'}
              </Typography>
              <Typography variant="h6" sx={{ color: colors.text.secondary, mb: 3 }}>
                {searchQuery || stepFilter !== 'all'
                  ? 'Essayez de modifier vos critères de recherche'
                  : 'Commencez par créer votre premier projet musical !'
                }
              </Typography>
              {!searchQuery && stepFilter === 'all' && (
                             <Button
                               variant="contained"
                  onClick={handleStartNew}
                  startIcon={<AddIcon />}
                               sx={{
                    bgcolor: colors.primary.main,
                    borderRadius: '20px',
                    px: 3,
                    py: 1,
                    fontSize: '1rem',
                                 fontWeight: 'bold',
                    '&:hover': { bgcolor: colors.primary.dark }
                               }}
                             >
                  Créer un projet
                             </Button>
            )}
          </CardContent>
        </Card>
        )}

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: colors.primary.main,
            '&:hover': {
              bgcolor: colors.primary.dark,
              transform: 'scale(1.1)'
            }
          }}
          onClick={handleStartNew}
        >
          <AddIcon />
        </Fab>

        <StartNewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onStartFromTheme={handleStartFromTheme}
          onStartFromLyric={handleStartFromLyric}
        />
      </Container>
    </div>
    );
}

export default ClipAudioProjectsScreen;
